import React, { useMemo } from 'react';
import { Phone, ArrowRight, Sparkles } from 'lucide-react';
import BookingForm from './BookingForm';

const BookingSection = () => {
  // Dynamic month and spots calculation
  const { currentMonth, spotsLeft } = useMemo(() => {
    const now = new Date();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const month = monthNames[now.getMonth()];
    
    // Generate pseudo-random spots (2-5) based on day of month
    const dayOfMonth = now.getDate();
    const spots = ((dayOfMonth * 13) % 4) + 2; // Results in 2, 3, 4, or 5
    
    return { currentMonth: month, spotsLeft: spots };
  }, []);

  return (
    <section className="bg-[#0a0a0b] py-24 px-6" id="book-call">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 text-sm text-green-400 mb-6 animate-pulse">
              <Sparkles className="w-4 h-4" />
              Only {spotsLeft} spots left for {currentMonth}
            </div>
            
            <h2 className="text-4xl md:text-5xl text-white font-normal mb-6">
              Ready to build your <span className="italic font-light">dream team?</span>
            </h2>
            
            <p className="text-gray-400 text-lg mb-8 leading-relaxed">
              Join 500+ companies who have transformed their engineering capabilities with Ruonn. 
              Book a free consultation and get matched with top talent within 72 hours.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mt-0.5">
                  <ArrowRight className="w-3 h-3 text-green-400" />
                </div>
                <div>
                  <div className="text-white font-medium">100% Satisfaction Guarantee</div>
                  <div className="text-gray-400 text-sm">Full replacement if you're not completely satisfied</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mt-0.5">
                  <ArrowRight className="w-3 h-3 text-green-400" />
                </div>
                <div>
                  <div className="text-white font-medium">No Upfront Payment</div>
                  <div className="text-gray-400 text-sm">Only pay after you're satisfied with your hire</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mt-0.5">
                  <ArrowRight className="w-3 h-3 text-green-400" />
                </div>
                <div>
                  <div className="text-white font-medium">Fast Matching</div>
                  <div className="text-gray-400 text-sm">Pre-vetted candidates in your inbox within 72 hours</div>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="flex items-center gap-4 pt-6 border-t border-white/5">
              <div className="flex -space-x-2">
                {['JD', 'SM', 'RK', 'AL'].map((initials, i) => (
                  <div key={i} className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white text-xs font-medium border-2 border-[#0a0a0b]">
                    {initials}
                  </div>
                ))}
              </div>
              <div className="text-sm">
                <span className="text-white font-medium">500+ companies</span>
                <span className="text-gray-400"> trust Ruonn</span>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-3xl blur-xl"></div>
            <div className="relative">
              <BookingForm />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
